import React, { useState } from "react";
import { KeyRound, RefreshCw, Eye, EyeOff, Copy, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, safeCopy, waToCustomer, getErr } from "../lib/api";

/**
 * Portal credentials block for admin lead/customer detail pages.
 * Fetches plain password on demand via /admin/{leads|customers}/{id}/credentials.
 * Provides: show/hide, copy, reset, share via WhatsApp.
 *
 * Props:
 *  - kind: "leads" | "customers"
 *  - id: the lead or customer id
 *  - customerName: full_name (for WA message)
 *  - whatsappTarget: mobile/whatsapp number for the share button
 *  - portalUrl: e.g. "https://.../login"
 */
export default function PortalCredentialsCard({ kind, id, customerName, whatsappTarget, portalUrl }) {
  const [loading, setLoading] = useState(false);
  const [creds, setCreds] = useState(null); // { login_id, password, has_password }
  const [showPw, setShowPw] = useState(false);
  const [resetting, setResetting] = useState(false);

  const fetchCreds = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/${kind}/${id}/credentials`);
      setCreds(data);
      setShowPw(true);
    } catch (e) { toast.error(getErr(e, "Could not load credentials")); }
    finally { setLoading(false); }
  };

  const resetPw = async () => {
    if (!window.confirm("Generate a new password? The old one will stop working immediately.")) return;
    setResetting(true);
    try {
      const { data } = await api.post(`/admin/${kind}/${id}/reset-password`);
      setCreds({ login_id: data.login_id, password: data.password, has_password: true });
      setShowPw(true);
      toast.success("New password generated");
    } catch (e) { toast.error(getErr(e, "Reset failed")); }
    finally { setResetting(false); }
  };

  const copy = async (text, label) => {
    const ok = await safeCopy(text);
    if (ok) toast.success(`${label} copied`);
    else toast.error("Couldn't copy — select & copy manually.");
  };

  const buildWaMessage = () => {
    const name = customerName || "there";
    const url = portalUrl || `${window.location.origin}/login`;
    return `Hello ${name},\n\nYour FinSahayak portal access is ready 🔑\n\n*Login ID:* ${creds?.login_id}\n*Password:* ${creds?.password}\n\nLog in here: ${url}\n\nPlease keep these confidential. Reply if you need any help.\n\n— Team FinSahayak`;
  };

  return (
    <div className="bento-card p-6" data-testid={`portal-creds-${kind}`}>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-xl bg-[#D17B5D]/15 text-[#D17B5D] grid place-items-center"><KeyRound size={16} /></div>
        <div>
          <span className="label-eyebrow text-[10px]">Portal access</span>
          <h3 className="font-display font-medium text-base text-[#121C17] -mt-0.5">Customer login credentials</h3>
        </div>
      </div>

      {!creds ? (
        <div className="space-y-3">
          <p className="text-xs text-[#5C6661]">Login ID is the customer's mobile number. The password is generated on lead creation and stored for admin reference. Click below to reveal.</p>
          <button onClick={fetchCreds} disabled={loading} data-testid="creds-reveal"
            className="btn-primary rounded-full px-4 py-2 text-xs font-medium inline-flex items-center gap-2 disabled:opacity-60">
            {loading ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />} Reveal credentials
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="bg-[#F3F1EC] rounded-xl p-3">
            <div className="text-[10px] uppercase tracking-wider text-[#5C6661]">Login ID (mobile)</div>
            <div className="mt-1 flex items-center gap-2">
              <span className="font-mono text-sm text-[#121C17]" data-testid="creds-login-id">{creds.login_id}</span>
              <button onClick={() => copy(creds.login_id, "Login ID")} data-testid="creds-copy-id"
                className="ml-auto rounded-full p-1.5 hover:bg-white" aria-label="Copy"><Copy size={12} /></button>
            </div>
          </div>

          <div className="bg-[#F3F1EC] rounded-xl p-3">
            <div className="text-[10px] uppercase tracking-wider text-[#5C6661]">Password</div>
            {creds.password ? (
              <div className="mt-1 flex items-center gap-2">
                <span className="font-mono text-sm text-[#121C17]" data-testid="creds-password">
                  {showPw ? creds.password : "•".repeat(creds.password.length)}
                </span>
                <button onClick={() => setShowPw((v) => !v)} data-testid="creds-toggle-pw" className="rounded-full p-1.5 hover:bg-white" aria-label="Show/hide">
                  {showPw ? <EyeOff size={12} /> : <Eye size={12} />}
                </button>
                <button onClick={() => copy(creds.password, "Password")} data-testid="creds-copy-pw" className="ml-auto rounded-full p-1.5 hover:bg-white" aria-label="Copy"><Copy size={12} /></button>
              </div>
            ) : (
              <div className="mt-1 text-xs text-[#5C6661]">No plaintext password stored. Click "Reset" to generate one.</div>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {creds.password && (
              <a
                href={waToCustomer(whatsappTarget, buildWaMessage())}
                target="_blank" rel="noopener noreferrer"
                data-testid="creds-wa-share"
                className="rounded-full px-4 py-2 text-xs font-medium text-white inline-flex items-center gap-1.5"
                style={{ background: "#25D366" }}>
                <Send size={12} /> Share on WhatsApp
              </a>
            )}
            <button onClick={resetPw} disabled={resetting} data-testid="creds-reset"
              className="rounded-full px-4 py-2 text-xs font-medium border border-[#E5E2DC] hover:bg-[#F3F1EC] inline-flex items-center gap-1.5 disabled:opacity-60">
              {resetting ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Reset password
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
